// src/utils/errorGuard.js
//
// Two problems solved here:
//
// 1. "A listener indicated an asynchronous response by returning true, but the
//    message channel closed before a response was received"
//    ── This comes from browser extensions (React DevTools, LastPass, uBlock,
//       etc.) injecting chrome.runtime message listeners into your page that
//       never resolve. It is NOT your code. It cannot be fixed in your app, but
//       it can be filtered out of the console and prevented from polluting your
//       error tracking (Sentry, etc.).
//
// 2. Unhandled promise rejections from cancelled axios requests (e.g. when the
//    user switches time frames quickly before the previous fetch finishes).
//    ── These show up as red errors in the console even though they are benign
//       cancellations. We install a global handler that ignores known-safe
//       rejections and logs everything else properly.
//
// Usage: import './utils/errorGuard' at the top of src/index.js (once).

// ─────────────────────────────────────────────────────────────────────────────
//  KNOWN-SAFE ERROR PATTERNS
//  Any rejection message that matches one of these strings is silently ignored.
//  Add to this list as you discover other extension-sourced noise.
// ─────────────────────────────────────────────────────────────────────────────
const IGNORED_MESSAGES = [
  // Chrome extension message channel noise
  'message channel closed before a response was received',
  'A listener indicated an asynchronous response by returning true',
  // Axios request cancelled (AbortController or CancelToken)
  'canceled',
  'CanceledError',
  // ResizeObserver fires occasionally during layout thrashing — harmless
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

function isIgnoredMessage(message) {
  if (!message) return false;
  const lower = String(message).toLowerCase();
  return IGNORED_MESSAGES.some((pattern) =>
    lower.includes(pattern.toLowerCase())
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  GLOBAL UNHANDLED PROMISE REJECTION HANDLER
//  Installed once on module load. Prevents benign rejections from showing as
//  red errors in the console and from being sent to error-tracking services.
// ─────────────────────────────────────────────────────────────────────────────
function installUnhandledRejectionGuard() {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      reason?.message ??
      reason?.toString?.() ??
      String(reason ?? '');

    if (isIgnoredMessage(message)) {
      // Suppress: prevent the browser from logging it as an error and stop
      // it from reaching Sentry / Datadog / etc.
      event.preventDefault();
      return;
    }

    // Everything else: let it propagate normally so real bugs are still caught.
    // You can add your own error-reporting call here, e.g.:
    // reportError(reason);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
//  CONSOLE.ERROR FILTER
//  Patches console.error so the chrome.runtime noise never appears in the
//  DevTools console at all (even in development).
//  We keep a reference to the original so real errors still print normally.
// ─────────────────────────────────────────────────────────────────────────────
function installConsoleErrorFilter() {
  const originalError = console.error.bind(console);

  console.error = (...args) => {
    const message = args
      .map((a) => (typeof a === 'object' ? a?.message ?? '' : String(a ?? '')))
      .join(' ');

    if (isIgnoredMessage(message)) return; // swallow silently

    originalError(...args); // pass everything else through unchanged
  };
}

// ─────────────────────────────────────────────────────────────────────────────
//  AXIOS ABORT HELPER
//  Use this in your components to cancel in-flight requests when the component
//  unmounts or when a dependency (like timeFrame) changes — preventing both the
//  "can't set state on unmounted component" warning AND the benign
//  CanceledError rejections that the guard above filters out.
//
//  Usage:
//    const { signal, abort } = makeAbortable();
//    axios.get(url, { signal }).then(...).catch(ignoreAbort);
//    return () => abort();   // cleanup in useEffect
// ─────────────────────────────────────────────────────────────────────────────
export function makeAbortable() {
  const controller = new AbortController();
  return {
    signal: controller.signal,
    abort:  () => controller.abort(),
  };
}

/**
 * Pass to .catch() to silently swallow axios CanceledError / AbortError.
 * Re-throws anything else so real errors still surface.
 *
 * Example:
 *   axios.get(url, { signal }).then(handler).catch(ignoreAbort);
 */
export function ignoreAbort(err) {
  if (
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError'    ||
    err?.code === 'ERR_CANCELED'
  ) {
    return; // benign — request was intentionally cancelled
  }
  throw err; // real error — let it propagate
}

// ─────────────────────────────────────────────────────────────────────────────
//  INSTALL  (called automatically on import)
// ─────────────────────────────────────────────────────────────────────────────
installUnhandledRejectionGuard();
installConsoleErrorFilter();